import React from 'react'
import { Box, Paper, Typography, Button } from '@mui/material'
import { CodeBlock } from '../common/CodeBlock/CodeBlock'
import { isJSON, mask } from '../../utils'
import { CONTENT } from '../../constants/constants'
import { EncryptedCell } from '../common/EncryptedCell'


/**
 * ConfigDetailPanel component displays the configuration details for a specific port.
 *
 * @component
 * @param {Object} props - The props object.
 * @param {number} props.portNumber - The port number for which the configuration details are displayed.
 * @param {Array} props.portConfigs - An array of configuration objects for the port.
 * @param {Function} props.handleEditClick - Callback function to handle the edit button click for a specific configuration.
 * @param {Function} props.handleDeleteClick - Callback function to handle the delete button click for a specific configuration.
 *
 * @returns {JSX.Element} A React component that renders the configuration details for a port.
 */
export default function ConfigDetailPanel({
  portNumber,
  portConfigs,
  handleEditClick,
  handleDeleteClick,
}) {
  return (
    <Paper
      sx={{
        p: 3,
        m: 2,
        overflow: 'auto',
        borderRadius: 2,
        boxShadow: 3,
        minWidth: 340,
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        <strong>Port {portNumber} Config Details</strong>
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Showing Monitor and TCP configs for this port
          </Typography>
        </Box>
      </Box>
      {portConfigs.map((config, idx) => {
        let parsedValue
        if (isJSON(config.value)) {
          parsedValue = JSON.parse(config.value)
        } else {
          parsedValue = config.value
        }
        const isTcpConfig = config.key?.toLowerCase().startsWith('tcp-')
        return (
          <React.Fragment key={config.id}>
            <Paper
              sx={(theme) => ({
                mb: 2,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                transition: 'background 0.2s',
                width: '100%',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              })}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  mb: 1,
                  width: '100%',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  <strong>Key:</strong> {config.key}
                </Typography>
                {config.comment && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 2 }}
                  >
                    <strong>Comment:</strong> {config.comment}
                  </Typography>
                )}
                <EncryptedCell encrypted={config.encrypted} />
              </Box>
              <Box sx={{ position: 'relative', mb: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <strong>
                    Value: {!!config.encrypted && mask(config?.value)}
                  </strong>
                </Typography>
                {!config.encrypted &&
                  (typeof parsedValue === 'object' ? (
                    <CodeBlock isJSON>
                      {JSON.stringify(parsedValue, null, 2)}
                    </CodeBlock>
                  ) : (
                    <CodeBlock>{parsedValue}</CodeBlock>
                  ))}
              </Box>
              <Box
                mt={2}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                {/* Note about encrypted fields */}
                <Typography
                  variant="caption"
                  textAlign="left"
                  color={'text.secondary'}
                  sx={{ flex: 1 }}
                >
                  {CONTENT.general.encrypted_note}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    size='small'
                    variant="outlined"
                    color={isTcpConfig ? 'primary' : 'secondary'}
                    onClick={() => handleEditClick(config.id)()}
                    title={
                      isTcpConfig ? 'Edit TCP Config' : 'Edit Monitor Config'
                    }
                    sx={{ minWidth: 110 }}
                    disabled={!!config.encrypted}
                  >
                    {isTcpConfig ? 'Edit TCP Config' : 'Edit Monitor Config'}
                  </Button>
                  <Button
                    size='small'
                    variant="outlined"
                    color="error"
                    onClick={() => handleDeleteClick(config.id)()}
                    title={
                      isTcpConfig
                        ? 'Delete TCP Config'
                        : 'Delete Monitor Config'
                    }
                    sx={{ minWidth: 110 }}
                  >
                    {isTcpConfig ? 'Delete TCP Config' : 'Delete Monitor Config'}
                  </Button>
                </Box>
              </Box>
            </Paper>
            {idx < portConfigs.length - 1 && (
              <Box
                sx={(theme) => ({
                  borderBottom: `1px dashed ${theme.palette.divider}`,
                  my: 2,
                })}
              />
            )}
          </React.Fragment>
        )
      })}
    </Paper>
  )
}
